/*
 * Reusable cell components for variant-focused tables. Add shared variant cell
 * renderers here when multiple tables need the same formatting or link behavior.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';

// Renders a variant value as an internal link while preserving the table row context for the details page.
export const VariantDetailsLink = ({ value, row, breadCrumbs }) => {
  if (!value) {
    return null;
  }

  return (
    <RouterLink
      to={`/variantdetails/${row.variantId}`}
      state={{
        variantId: row.variantId,
        variantName: row.variantName,
        reviewStatus: row.reviewStatus?.value,
        breadCrumbs,
        expected: row.expected,
        completed: row.completed,
        inProgress: row.inProgress,
      }}
    >
      {value}
    </RouterLink>
  );
};

VariantDetailsLink.propTypes = {
  value: PropTypes.string,
  row: PropTypes.object,
  breadCrumbs: PropTypes.array,
};

// Renders a dbSNP rs number as an external NCBI SNP link.
export const DbSnpLink = ({ rsNumber }) => {
  if (!rsNumber || rsNumber === '.') {
    return null;
  }

  const value = String(rsNumber).trim();

  return (
    <Link href={`https://www.ncbi.nlm.nih.gov/snp/${value}`} target="_blank" rel="noopener noreferrer">
      {value}
    </Link>
  );
};

DbSnpLink.propTypes = {
  rsNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// Normalizes comma-separated gene values so table cells display consistent spacing.
export const GeneCell = ({ gene }) => {
  if (!gene) {
    return null;
  }

  return String(gene).split(',').join(', ');
};

GeneCell.propTypes = {
  gene: PropTypes.string,
};

// Adds soft wrap opportunities after commas without forcing line breaks.
export const CommaBreakText = ({ value }) => {
  if (!value) {
    return null;
  }

  const parts = String(value).split(',');

  return parts.map((part, index) => (
    <React.Fragment key={`${part}-${index}`}>
      <span style={{ whiteSpace: 'nowrap' }}>
        {part.trim()}
        {index < parts.length - 1 ? ',' : null}
      </span>
      {index < parts.length - 1 ? (
        <>
          <wbr />{' '}
        </>
      ) : null}
    </React.Fragment>
  ));
};

CommaBreakText.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
